/**
 * Reads a courier's COD remittance statement into lines the API can match.
 *
 * Accepts Shiprocket's remittance report as exported (CSV with a header row),
 * or lines pasted by hand:
 *
 *   AWB,Amount            → a header is recognised by name
 *   14344512345,520.00    → a parcel and the rupees paid for it
 *   14344512346           → a parcel on its own (amount not checked)
 *
 * The parcel column is the AWB when there is one, else the order id (which, for
 * Shiprocket, is the sub-order id we sent it). The amount column is the COD
 * amount; amounts are rupees and become paise here.
 */
export interface StatementLine {
  ref: string;
  amountPaise?: number;
}

export interface ParsedStatement {
  lines: StatementLine[];
  /** Lines that were not blank but could not be read, with their line number. */
  skipped: Array<{ line: number; text: string }>;
  /** Which columns were used, for showing the operator. */
  columns: { ref: string; amount: string | null };
}

/** One CSV row, honouring quotes ("1,234.00" stays one field). */
function splitCsvRow(row: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < row.length; i++) {
    const c = row[i]!;
    if (c === '"') {
      if (quoted && row[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
    } else if ((c === ',' || c === '\t' || c === ';') && !quoted) {
      out.push(cur.trim());
      cur = '';
    } else cur += c;
  }
  out.push(cur.trim());
  return out;
}

/** Rupees as written in a statement ("₹1,234.50", "520") → paise, or undefined. */
export function rupeesToPaise(raw: string): number | undefined {
  const cleaned = raw.replace(/[₹,\s]/g, '').replace(/^rs\.?/i, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return undefined;
  const [whole, frac = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

const norm = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '');

function findColumn(headers: string[], tests: Array<(h: string) => boolean>): number {
  for (const test of tests) {
    const i = headers.findIndex((h) => test(norm(h)));
    if (i >= 0) return i;
  }
  return -1;
}

export function parseRemittanceStatement(text: string): ParsedStatement {
  const rows = text
    .split(/\r?\n/)
    .map((r, i) => ({ line: i + 1, cells: splitCsvRow(r), text: r.trim() }))
    .filter((r) => r.text.length > 0);

  const skipped: ParsedStatement['skipped'] = [];
  if (rows.length === 0) return { lines: [], skipped, columns: { ref: 'AWB', amount: null } };

  // A header row is one whose cells are mostly words, not parcel numbers.
  const first = rows[0]!.cells;
  const looksLikeHeader = first.some((c) => /[a-z]{3,}/i.test(c) && !/\d{6,}/.test(c));

  let refCol = 0;
  let amountCol = first.length > 1 ? 1 : -1;
  let refName = 'AWB';
  let amountName: string | null = amountCol >= 0 ? 'Amount' : null;
  let body = rows;

  if (looksLikeHeader) {
    const awb = findColumn(first, [(h) => h.includes('awb'), (h) => h.includes('tracking')]);
    const orderId = findColumn(first, [
      (h) => h === 'orderid' || h === 'channelorderid',
      (h) => h.includes('orderid'),
    ]);
    refCol = awb >= 0 ? awb : orderId >= 0 ? orderId : 0;
    amountCol = findColumn(first, [
      (h) => h.includes('cod') && h.includes('amount'),
      (h) => h.includes('remitted') || h.includes('remittance'),
      (h) => h.includes('amount'),
      (h) => h.includes('cod'),
    ]);
    refName = first[refCol] ?? 'AWB';
    amountName = amountCol >= 0 ? (first[amountCol] ?? null) : null;
    body = rows.slice(1);
  }

  const lines: StatementLine[] = [];
  for (const r of body) {
    const ref = (r.cells[refCol] ?? '').replace(/^'/, '').trim();
    if (!ref || /\s/.test(ref) || ref.length > 100) {
      skipped.push({ line: r.line, text: r.text });
      continue;
    }
    const rawAmount = amountCol >= 0 ? (r.cells[amountCol] ?? '') : '';
    const amountPaise = rawAmount ? rupeesToPaise(rawAmount) : undefined;
    if (rawAmount && amountPaise === undefined) {
      skipped.push({ line: r.line, text: r.text });
      continue;
    }
    lines.push(amountPaise === undefined ? { ref } : { ref, amountPaise });
  }

  return { lines, skipped, columns: { ref: refName, amount: amountName } };
}
